import { useState } from "react";
import { FormattedMessage } from "react-intl";
import * as Dialog from "@radix-ui/react-dialog";

import { Button } from "@/components/ui/Button";

import cn from "@/utils/cn";

import {
  SUMMIT_TOUR_IMAGES,
  SUMMIT_TOUR_MOBILE_IMAGES,
} from "../summitTourImages";

import { useIsMobile } from "@/hooks/useIsMobile";

type SummitTourStep = {
  image: string;
  imageMobile?: string;
  title: React.ReactNode;
  subtitle: React.ReactNode;
};

const [step1Splash, step2Splash, step3Splash, step4Splash] = SUMMIT_TOUR_IMAGES;
const [step2SplashMobile, step3SplashMobile] = SUMMIT_TOUR_MOBILE_IMAGES;

const TOUR_STEPS: SummitTourStep[] = [
  {
    image: step1Splash,
    title: (
      <FormattedMessage
        defaultMessage="Welcome to Summit"
        description="Title for step 1 of the Summit product tour"
      />
    ),
    subtitle: (
      <FormattedMessage
        defaultMessage="Everything you need to understand a trip—all in one place."
        description="Subtitle for step 1 of the Summit product tour"
      />
    ),
  },
  {
    image: step2Splash,
    imageMobile: step2SplashMobile,
    title: (
      <FormattedMessage
        defaultMessage="Follow every journey"
        description="Placeholder title for step 2 of the Summit product tour"
      />
    ),
    subtitle: (
      <FormattedMessage
        defaultMessage="See the route, status, origin, destination, and timing at a glance."
        description="Placeholder subtitle for step 2 of the Summit product tour"
      />
    ),
  },
  {
    image: step3Splash,
    imageMobile: step3SplashMobile,
    title: (
      <FormattedMessage
        defaultMessage="Spot what needs attention"
        description="Placeholder title for step 3 of the Summit product tour"
      />
    ),
    subtitle: (
      <FormattedMessage
        defaultMessage="Explore alerts, sensors, attributes, assets, and key trip events."
        description="Placeholder subtitle for step 3 of the Summit product tour"
      />
    ),
  },
  {
    image: step4Splash,
    title: (
      <FormattedMessage
        defaultMessage="Events meet sensor data"
        description="Placeholder title for step 4 of the Summit product tour"
      />
    ),
    subtitle: (
      <FormattedMessage
        defaultMessage="Pair trip timeline moments with the sensor readings that explain them."
        description="Placeholder subtitle for step 4 of the Summit product tour"
      />
    ),
  },
];

type Props = {
  onClose: () => void;
  onSkip: () => void;
};

export const SummitProductTourModal = ({ onClose, onSkip }: Props) => {
  const [stepIndex, setStepIndex] = useState(0);
  const isMobile = useIsMobile();
  const step = TOUR_STEPS[stepIndex];
  const imageSrc = isMobile && step.imageMobile ? step.imageMobile : step.image;
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === TOUR_STEPS.length - 1;

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onSkip();
    }
  };

  return (
    <Dialog.Root open onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        {/* Above the tablet top menu bar (z-[9999999999] in ProtectedRouteLayout). */}
        <Dialog.Overlay
          className="fixed inset-0 z-[10000000000] bg-chorus-black-100/50"
          data-testid="summit-product-tour-overlay"
        />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-[10000000001] flex h-[664px] w-full max-w-[800px] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden bg-[#F4F6F8] py-6 shadow-2xl md:rounded-xl"
          data-testid="summit-product-tour"
          onInteractOutside={(event) => {
            event.preventDefault();
            onClose();
          }}
        >
          <div className="flex h-[280px] flex-1 items-center justify-center bg-chorus-gray-50 md:items-end">
            <img
              alt="Summit tour step image"
              className="max-h-full w-auto object-contain"
              src={imageSrc}
            />
          </div>

          <div className="mt-2 space-y-2 px-6 text-center">
            <Dialog.Title className="font-sora text-display-sm text-chorus-black-500">
              {step.title}
            </Dialog.Title>
            <Dialog.Description className="mx-auto font-manrope text-chorus-gray-800">
              {step.subtitle}
            </Dialog.Description>
          </div>

          <div className="flex h-[117px] shrink-0 items-center px-6 sm:px-10 md:px-[156px]">
            <div className="flex min-w-0 flex-1 items-center justify-start">
              {isFirstStep ? (
                <button
                  className="flex h-11 items-center font-manrope text-sm text-chorus-gray-600 transition-colors hover:text-chorus-gray-800"
                  data-testid="summit-product-tour-skip"
                  onClick={onSkip}
                  type="button"
                >
                  <FormattedMessage
                    defaultMessage="Skip"
                    description="Skip button on the first step of the Summit product tour"
                  />
                </button>
              ) : (
                <button
                  className="flex h-11 items-center font-manrope text-sm text-chorus-gray-600 transition-colors hover:text-chorus-gray-800"
                  data-testid="summit-product-tour-back"
                  onClick={() => setStepIndex((current) => current - 1)}
                  type="button"
                >
                  <FormattedMessage
                    defaultMessage="Back"
                    description="Back button on the Summit product tour"
                  />
                </button>
              )}
            </div>

            <div
              aria-label="Tour progress"
              className="flex shrink-0 items-center gap-2.5"
              data-testid="summit-product-tour-dots"
            >
              {TOUR_STEPS.map((_, index) => (
                <span
                  className={cn(
                    "size-2.5 rounded-full",
                    index === stepIndex
                      ? "bg-chorus-teal-500"
                      : "bg-chorus-gray-200",
                  )}
                  data-testid={`summit-product-tour-dot-${index + 1}`}
                  data-active={index === stepIndex ? "true" : "false"}
                  key={index}
                />
              ))}
            </div>

            <div className="flex min-w-0 flex-1 items-center justify-end">
              <Button
                className="h-11 min-w-[106px] shadow-lg"
                data-testid="summit-product-tour-next"
                onClick={() => {
                  if (isLastStep) {
                    onClose();
                    return;
                  }
                  setStepIndex((current) => current + 1);
                }}
              >
                {isLastStep ? (
                  <FormattedMessage
                    defaultMessage="Explore Summit"
                    description="Explore Summit button on the last step of the Summit product tour"
                  />
                ) : (
                  <FormattedMessage
                    defaultMessage="Next"
                    description="Next button on the Summit product tour"
                  />
                )}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
